import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MainCurrencyDto } from '../_core/_model/currency';
import { UserDto } from '../_core/_model/users';
import { WalletDto } from '../_core/_model/wallet';
import { ApiService } from '../_core/_services/api.service';
import { ModalCleanupService } from '../_core/_services/modal-cleanup.service';
import { NotificationService } from '../_core/_services/notification.service';

declare const bootstrap: any;

@Component({
  selector: 'app-wallets',
  standalone: false,
  templateUrl: './wallets.html',
  styleUrl: './wallets.css',
})
export class Wallets implements OnInit {
  userData: UserDto | null = null;
  wallets: WalletDto[] = [];
  currencies: MainCurrencyDto[] = [];

  createWalletForm: FormGroup;

  loading = false;
  loadingCurrencies = false;
  creatingWallet = false;
  submittedWallet = false;
  walletExistsWarning: string | null = null;
  error = '';

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private api: ApiService,
    private notify: NotificationService,
    private modalCleanup: ModalCleanupService,
  ) {
    this.createWalletForm = this.fb.group({
      currency: ['', Validators.required],
    });

    this.createWalletForm.get('currency')!.valueChanges.subscribe((code: string) => {
      this.updateWalletExistsWarning(code);
    });
  }

  ngOnInit(): void {
    this.loadMain();
    this.loadWallets();

    this.route.queryParamMap.subscribe(params => {
      const currency = params.get('currency');
      if (!currency) return;

      this.createWalletForm.patchValue({ currency });
      this.updateWalletExistsWarning(currency);
      setTimeout(() => this.openCreateWalletModal());
    });
  }

  loadMain(): void {
    this.loadingCurrencies = true;

    this.api.getMain().subscribe({
      next: (res) => {
        this.currencies = (res.data?.currencies ?? []).filter(c => c.status === 'Active');
      },
      error: (e) => {
        this.notify.showError(
          e?.error?.Error?.Details ?? 'Failed to load currencies',
          'Wallets'
        );
      },
      complete: () => (this.loadingCurrencies = false),
    });
  }

  loadWallets(): void {
    this.loading = true;
    this.error = '';

    this.api.getProfile().subscribe({
      next: (res) => {
        this.userData = res.data ?? null;
        this.wallets = this.userData?.wallets ?? [];
        this.updateWalletExistsWarning(this.createWalletForm.get('currency')!.value);
      },
      error: (e) => {
        this.error = e?.error?.Error?.Details ?? 'Failed to load wallets';
        this.notify.showError(this.error, 'Wallets');
      },
      complete: () => (this.loading = false),
    });
  }

  createWallet(): void {
    this.submittedWallet = true;
    this.walletExistsWarning = null;

    if (this.createWalletForm.invalid) return;

    const currency: string = this.createWalletForm.get('currency')!.value;
    const exists = this.wallets?.some(w => w.currency.code === currency);
    if (exists) {
      this.walletExistsWarning = currency;
      return;
    }

    this.creatingWallet = true;

    this.api.createWallet(currency).subscribe({
      next: () => {
        this.notify.showSuccess(`Wallet created: ${currency}`, 'Wallets');
        this.modalCleanup.hideById('createWalletModal');

        this.createWalletForm.reset({ currency: '' });
        this.submittedWallet = false;
        this.loadWallets();
      },
      error: (e) => {
        const msg = e?.error?.Error?.Details ?? 'Failed to create wallet';
        this.notify.showError(msg, 'Wallet Error');
      },
      complete: () => (this.creatingWallet = false),
    });
  }

  refresh(): void {
    this.loadWallets();
  }

  private openCreateWalletModal(): void {
    const el = document.getElementById('createWalletModal');
    if (!el) return;

    const modal = bootstrap?.Modal.getInstance(el) || new bootstrap.Modal(el);
    modal.show();
  }

  private updateWalletExistsWarning(code: string | null): void {
    this.walletExistsWarning = null;
    if (!code) return;

    const exists = this.wallets?.some(w => w.currency.code === code);
    if (exists) this.walletExistsWarning = code;
  }
}
